import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  ChevronsDownUp,
  ChevronsUpDown,
  Maximize,
  MousePointer2,
  SquareDashed,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import {
  useGetPositionsForIncident,
  useGetSeatResourcesForRoster,
  type IncidentPosition,
  type SeatResource,
} from 'hooks/incident/useRoster';
import EditPositionModal from 'components/IncidentConfig/EditPositionModal';
import {
  registerHwcgTourApi,
  rosterSeatLoad,
} from 'components/IncidentConfig/hwcgTour/hwcgTourBridge';
import RosterResourceModal from 'components/IncidentConfig/roster/RosterResourceModal';
import AddChildPositionDialog from 'components/IncidentConfig/roster/AddChildPositionDialog';
import AddResourceToRosterDialog from 'components/IncidentConfig/roster/AddResourceToRosterDialog';
import type {
  FlowPoint,
  RosterEdgeAttachments,
} from 'components/IncidentConfig/roster/rosterEdgeAttach';
import {
  LAYOUT_STYLE_OPTIONS,
  applyRosterLayoutStyle,
  layoutIcsDefaultOrgChart,
  rosterEdgeId,
  type LayoutNode,
  type RosterLayoutStyle,
} from 'components/IncidentConfig/roster/rosterLayoutStyles';
import {
  polylineToPath,
  routeConnectors,
  type ChartRect,
} from 'components/IncidentConfig/roster/rosterConnectorRouting';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { Button } from '@/components/ui/button';
import {
  hwcgSeatStatusCounts,
  hwcgSeatStatusLine,
  type HwcgPersonSeatStatus,
} from 'components/IncidentConfig/roster/rosterPeriods';
import SeatBoxCard, {
  RosterChartColorModeContext,
  estimateNodeHeight,
  type RosterChartColorMode,
  type SeatBoxNodeData,
} from 'components/IncidentConfig/roster/SeatBoxNode';
import { NODE_WIDTH } from 'components/IncidentConfig/roster/seatBoxMetrics';
import { getSectionTheme } from 'components/IncidentConfig/rosterOrgChartTheme';
import {
  isTemaLouSeat,
  isTemaRccSeat,
} from 'components/IncidentConfig/roster/rosterLayoutTiers';
import {
  boundsOfRects,
  captureOrgChartImage,
} from 'components/IncidentConfig/captureOrgChartImage';
import { useChartViewport } from 'components/IncidentConfig/roster/useChartViewport';
import {
  collapseRosterTree,
  persistCollapsedSeats,
  readCollapsedSeats,
} from 'components/IncidentConfig/roster/rosterCollapse';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { resolveCategoryLabel } from 'components/IncidentConfig/positionCategories';

/**
 * Which roster's occupants the chart draws, and which positions survive the
 * filter. The options are independent: `active` and `draft` each add a roster's
 * occupants to the boxes, while `empty` keeps positions holding nothing, which
 * is how an unassigned seat is reached to fill it.
 */
export type RosterOccupancyOption = 'active' | 'draft' | 'empty';

export const DEFAULT_OCCUPANCY: RosterOccupancyOption[] = [
  'active',
  'draft',
  'empty',
];

/**
 * Left-drag either pans the canvas or draws a selection rectangle. The two
 * cannot share the button, so the toolbar picks which one is armed.
 */
type CanvasInteractionMode = 'select' | 'box';

/** Resolved from roster color-mode tokens on the org-chart wrapper. */
const EDGE_STROKE_COLOR = 'var(--roster-edge-stroke, #64748b)';
/**
 * The grid is orientation, not content: a faint sub-pixel dot at a wide pitch
 * shows the canvas moving under a pan without competing with the seats.
 */
const DOT_GRID_COLOR =
  'color-mix(in srgb, var(--roster-dot-grid, #94a3b8) 30%, transparent)';
const DOT_GRID_RADIUS = 0.5;
const DOT_GRID_GAP = 28;

/** Pointer travel before a press counts as a drag rather than a click. */
const DRAG_THRESHOLD = 3;

/** Marks canvas chrome so a press on it neither pans nor selects. */
const CHROME_ATTRIBUTE = 'data-roster-chart-chrome';
/** Marks a seat so a press on it is left to the seat itself. */
const SEAT_ATTRIBUTE = 'data-roster-seat';

// ─── Pure helpers ─────────────────────────────────────────────────────────────

type AnyPosition = { id: string; name: string; parent_id: string | null };

/**
 * True if any descendant of posId holds an item, personnel or resource, on the
 * selected roster(s).
 */
function hasDescendantWithItem(
  posId: string,
  occupiedIds: Set<string>,
  childrenOf: Map<string, AnyPosition[]>
): boolean {
  for (const child of childrenOf.get(posId) ?? []) {
    if (occupiedIds.has(child.id)) return true;
    if (hasDescendantWithItem(child.id, occupiedIds, childrenOf)) return true;
  }
  return false;
}

type Size = { width: number; height: number };

type ChartNode = {
  id: string;
  position: FlowPoint;
  data: SeatBoxNodeData;
};

type ScreenRect = { x: number; y: number; width: number; height: number };

const rectsIntersect = (a: ScreenRect, b: ScreenRect) =>
  a.x < b.x + b.width &&
  b.x < a.x + a.width &&
  a.y < b.y + b.height &&
  b.y < a.y + a.height;

/**
 * Real rendered size of every seat card, fed back into the layout.
 *
 * One ResizeObserver watches every card wrapper. Sizes are merged rather than
 * replaced, so a card that unmounts and remounts across a refetch keeps its
 * last known size instead of dropping back to the estimate for a frame.
 *
 * Settles rather than loops: a box's height follows its content and its fixed
 * width, never where the layout put it, so relaying out reports the same
 * measurement and the update is dropped.
 */
function useSeatMeasurements() {
  const [sizes, setSizes] = useState<Record<string, Size>>({});
  const observerRef = useRef<ResizeObserver | null>(null);
  const idByElement = useRef(new Map<Element, string>());
  const elementById = useRef(new Map<string, HTMLElement>());
  const refCache = useRef(new Map<string, (el: HTMLElement | null) => void>());

  const record = useCallback((updates: [string, Size][]) => {
    setSizes((current) => {
      let changed = false;
      const next = { ...current };
      for (const [id, size] of updates) {
        const width = Math.round(size.width);
        const height = Math.round(size.height);
        if (!width || !height) continue;
        const before = current[id];
        if (before?.width === width && before?.height === height) continue;
        next[id] = { width, height };
        changed = true;
      }
      return changed ? next : current;
    });
  }, []);

  const getObserver = useCallback(() => {
    if (observerRef.current || typeof ResizeObserver === 'undefined')
      return observerRef.current;
    observerRef.current = new ResizeObserver((entries) => {
      const updates: [string, Size][] = [];
      for (const entry of entries) {
        const id = idByElement.current.get(entry.target);
        const element = entry.target as HTMLElement;
        // offsetWidth/Height are layout sizes, untouched by the zoom transform
        // on the layer above.
        if (id)
          updates.push([
            id,
            { width: element.offsetWidth, height: element.offsetHeight },
          ]);
      }
      if (updates.length) record(updates);
    });
    return observerRef.current;
  }, [record]);

  /** Stable callback ref per seat, so React does not re-run it every render. */
  const seatRef = useCallback(
    (id: string) => {
      let ref = refCache.current.get(id);
      if (ref) return ref;
      ref = (element) => {
        const previous = elementById.current.get(id);
        if (previous && previous !== element) {
          observerRef.current?.unobserve(previous);
          idByElement.current.delete(previous);
          elementById.current.delete(id);
        }
        if (!element) return;
        elementById.current.set(id, element);
        idByElement.current.set(element, id);
        getObserver()?.observe(element);
        record([
          [id, { width: element.offsetWidth, height: element.offsetHeight }],
        ]);
      };
      refCache.current.set(id, ref);
      return ref;
    },
    [getObserver, record]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { sizes, seatRef };
}

const TOOLBAR_GROUP_CLASS =
  'tw:flex tw:gap-1 tw:rounded-md tw:border tw:border-border tw:bg-background/90 tw:p-1 tw:shadow-sm';

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Shared so that a query with no data yet hands back the same array every
 * render. A fresh `[]` per render would make the chart model, and so the node
 * and edge state built from it, look new on every pass and re-trigger itself.
 */
const NO_SEAT_RESOURCES: SeatResource[] = [];

/**
 * Same reasoning as NO_SEAT_RESOURCES, for a restyled chart's unused ICS
 * routing.
 */
const EMPTY_EDGE_ROUTES: Record<string, FlowPoint[]> = {};
const EMPTY_EDGE_ATTACHMENTS: Record<string, RosterEdgeAttachments> = {};
const NO_SELECTION: ReadonlySet<string> = new Set();

type RosterOrgChartProps = {
  incidentMembers: RosterEntry[];
  draftIncidentMembers: RosterEntry[] | null;
  /**
   * People whose first available HWCG period is later than the one in view.
   * When set, including an empty list, the draft roster is not drawn.
   */
  hwcgLaterMembers?: RosterEntry[] | null;
  /** Period a person joins, shown when the roster filter is All. */
  hwcgPeriodLabels?: Record<string, string>;
  /** Per-person activation, check-in, and sign-in used by the status row. */
  hwcgPersonStatus?: Record<string, HwcgPersonSeatStatus> | null;
  /** Which roster's occupants to draw, and whether empty seats are kept. */
  occupancyFilter?: readonly RosterOccupancyOption[];
  /** When set, only these seats (and their edges) are drawn. */
  visiblePositionIds?: Set<string> | null;
  /**
   * When set, only these seats open the position modal. Null leaves every
   * visible seat clickable.
   */
  editablePositionIds?: string[] | null;
  /** Org-chart light/dark mode from OrganizationChart. */
  colorMode?: RosterChartColorMode;
  /** Primary selected seat for template expansions / add-under-selected. */
  onSelectedPositionIdChange?: (positionId: string | null) => void;
  /**
   * Draw the chart for reference only. A form box prints the organisation
   * rather than being where it is built, so every affordance that writes is
   * withheld and the roster page stays the one place seats are changed.
   */
  readOnly?: boolean;
};

export type RosterOrgChartHandle = {
  /**
   * PNG of the whole chart as currently filtered, sized for the ICS-207 org
   * chart region.
   */
  captureImage: () => Promise<File | null>;
};

type DragState =
  | {
      kind: 'pan';
      pointerId: number;
      lastX: number;
      lastY: number;
      moved: boolean;
      button: number;
    }
  | {
      kind: 'marquee';
      pointerId: number;
      startX: number;
      startY: number;
      /** Selection to add to when Shift is held, otherwise empty. */
      base: ReadonlySet<string>;
    };

/**
 * The incident org chart: shadcn seat cards positioned by the ICS layout on a
 * pannable, zoomable canvas, joined by SVG connectors that are routed around
 * every box they are not attached to.
 */
const RosterOrgChart = forwardRef<RosterOrgChartHandle, RosterOrgChartProps>(
  function RosterOrgChart(
    {
      incidentMembers,
      draftIncidentMembers,
      hwcgLaterMembers = null,
      hwcgPeriodLabels,
      hwcgPersonStatus = null,
      occupancyFilter = DEFAULT_OCCUPANCY,
      visiblePositionIds = null,
      editablePositionIds = null,
      colorMode = 'dark',
      onSelectedPositionIdChange,
      readOnly = false,
    },
    ref
  ) {
    const incidentPositionsQuery = useGetPositionsForIncident();
    const { data: activeSeatResources = NO_SEAT_RESOURCES } =
      useGetSeatResourcesForRoster(false);
    const { data: draftSeatResources = NO_SEAT_RESOURCES } =
      useGetSeatResourcesForRoster(true);

    const [showEditPositionModalForPosition, setShowEditPositionModal] =
      useState('');
    const [seatModalTab, setSeatModalTab] = useState<'team' | 'planning'>(
      'team'
    );
    const positionsRef = useRef<IncidentPosition[]>([]);
    positionsRef.current = incidentPositionsQuery.data ?? [];
    const positionQueryRef = useRef(incidentPositionsQuery);
    positionQueryRef.current = incidentPositionsQuery;
    const editableIdsRef = useRef(editablePositionIds);
    editableIdsRef.current = editablePositionIds;

    useEffect(() => {
      return registerHwcgTourApi({
        openRosterSeat: () => {
          const editable = editableIdsRef.current;
          const positions = positionsRef.current;
          const query = positionQueryRef.current;
          rosterSeatLoad.chartCount = positions.length;
          rosterSeatLoad.chartFetching = query.isFetching;
          rosterSeatLoad.chartFetched = query.isFetched;
          if (!editable || editable.length === 0) return;
          const allowed = new Set(editable);
          const personnel = positions.filter(
            (pos) =>
              allowed.has(pos.id) &&
              !pos.isResourcePosition &&
              !(pos.is_template && pos.name === 'Qualified Individual')
          );
          const seat =
            personnel.find(
              (pos) => !pos.parent_id || !allowed.has(pos.parent_id)
            ) ?? personnel[0];
          if (!seat) return;
          setSeatModalTab('planning');
          setShowEditPositionModal(seat.id);
        },
      });
    }, []);
    const [openResourcePositionId, setOpenResourcePositionId] = useState('');
    const [addChildPositionParentId, setAddChildPositionParentId] = useState<
      string | null
    >(null);
    const [addChildResourceParentId, setAddChildResourceParentId] = useState<
      string | null
    >(null);
    const [interactionMode, setInteractionMode] =
      useState<CanvasInteractionMode>('select');
    const [layoutStyle, setLayoutStyle] =
      useState<RosterLayoutStyle>('centered');
    /**
     * Positions produced by a Centered / Left / Right restyle. Like the
     * connector routing below this is a presentation choice with nowhere to
     * live on the roster, so it is held for the session; a reload comes back to
     * the automatic ICS layout.
     */
    const [styleLayout, setStyleLayout] = useState<Record<
      string,
      FlowPoint
    > | null>(null);
    // Connector routing is a presentation choice with nowhere to live on the
    // roster, so it is kept for the session and rebuilt from the layout style.
    const [edgeRoutes, setEdgeRoutes] = useState<Record<string, FlowPoint[]>>(
      {}
    );
    const [edgeAttachments, setEdgeAttachments] = useState<
      Record<string, RosterEdgeAttachments>
    >({});
    /** Seats the user has picked; narrowed to the live chart in `selectedIds`. */
    const [pickedIds, setPickedIds] =
      useState<ReadonlySet<string>>(NO_SELECTION);
    const [marquee, setMarquee] = useState<ScreenRect | null>(null);
    const [isPanning, setIsPanning] = useState(false);

    const incidentId = useActualIncidentId();
    const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(() =>
      readCollapsedSeats(incidentId)
    );
    const collapseKeyRef = useRef(incidentId);
    useEffect(() => {
      if (collapseKeyRef.current === incidentId) return;
      collapseKeyRef.current = incidentId;
      setCollapsedIds(readCollapsedSeats(incidentId));
    }, [incidentId]);
    const updateCollapsed = useCallback(
      (next: (current: ReadonlySet<string>) => ReadonlySet<string>) => {
        setCollapsedIds((current) => {
          const value = next(current);
          persistCollapsedSeats(collapseKeyRef.current, value);
          return value;
        });
      },
      []
    );
    const toggleCollapsed = useCallback(
      (positionId: string) =>
        updateCollapsed((current) => {
          const next = new Set(current);
          if (next.has(positionId)) next.delete(positionId);
          else next.add(positionId);
          return next;
        }),
      [updateCollapsed]
    );

    const { sizes: measuredSizes, seatRef } = useSeatMeasurements();

    const containerRef = useRef<HTMLDivElement>(null);
    const viewportElementRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<DragState | null>(null);

    const {
      viewport,
      viewportRef,
      zoomIn,
      zoomInAt,
      zoomOut,
      panBy,
      fitBounds,
      toContainerPoint,
    } = useChartViewport(containerRef);

    const isBoxMode = interactionMode === 'box';

    // Drives whether Reset Layout has anything to undo, so it is not offered on a chart nobody has restyled.
    const hasCustomLayout =
      styleLayout !== null ||
      Object.keys(edgeRoutes).length > 0 ||
      Object.keys(edgeAttachments).length > 0;

    const showActive = occupancyFilter.includes('active');
    const showDraft = occupancyFilter.includes('draft');
    const keepEmpty = occupancyFilter.includes('empty');
    // Drawing both rosters into one box is only legible if each group says which roster it came from.
    const labelRosters = showActive && showDraft;

    // Recompute the chart whenever positions data, members, filter, or mode change.
    const chartModel = useMemo(() => {
      const raw = incidentPositionsQuery.data ?? [];
      const positions = raw.filter(
        (pos) => !(pos.is_template && pos.name === 'Qualified Individual')
      );

      const posById = new Map(positions.map((p) => [p.id, p]));

      const childrenOf = new Map<string, AnyPosition[]>();
      for (const pos of positions) {
        if (pos.parent_id) {
          const arr = childrenOf.get(pos.parent_id) ?? [];
          arr.push(pos);
          childrenOf.set(pos.parent_id, arr);
        }
      }

      const usingHwcgPeriods = hwcgLaterMembers != null;
      const selectedMembers = [
        ...(showActive ? incidentMembers : []),
        ...(usingHwcgPeriods
          ? (hwcgLaterMembers ?? [])
          : showDraft
            ? (draftIncidentMembers ?? [])
            : []),
      ];
      const selectedResources = [
        ...(showActive ? activeSeatResources : []),
        ...(showDraft ? draftSeatResources : []),
      ];

      // "Assigned" covers attached resources as well as people, so a seat holding only equipment still counts.
      const occupiedIds = new Set<string>();
      for (const member of selectedMembers) {
        for (const pos of member.incidentPositions ?? [])
          occupiedIds.add(pos.positionId);
      }
      for (const resource of selectedResources)
        occupiedIds.add(resource.positionId);

      const filtered = positions.filter(
        (pos) =>
          (visiblePositionIds == null || visiblePositionIds.has(pos.id)) &&
          (keepEmpty ||
            occupiedIds.has(pos.id) ||
            hasDescendantWithItem(pos.id, occupiedIds, childrenOf))
      );

      const collapse = collapseRosterTree(
        filtered,
        readOnly ? NO_SELECTION : collapsedIds
      );
      const shownIds = new Set(collapse.shown);
      const visible = filtered.filter((pos) => shownIds.has(pos.id));
      const visibleIds = new Set(visible.map((p) => p.id));

      // A resource has no seat to assign members to, so it opens the resource modal rather than the seat editor.
      const canOpen = (positionId: string) =>
        editablePositionIds == null || editablePositionIds.includes(positionId);

      const openModal = (positionId: string) => {
        // Mid-marquee a click is part of choosing seats, not a request to edit one.
        if (interactionMode === 'box') return;
        if (!canOpen(positionId)) return;
        if (posById.get(positionId)?.isResourcePosition) {
          setOpenResourcePositionId(positionId);
          return;
        }
        setSeatModalTab('team');
        setShowEditPositionModal(positionId);
      };

      const addChildPosition = (positionId: string) => {
        if (interactionMode === 'box') return;
        setAddChildPositionParentId(positionId);
      };

      const addChildResource = (positionId: string) => {
        if (interactionMode === 'box') return;
        setAddChildResourceParentId(positionId);
      };

      const membersFor = (list: RosterEntry[], positionId: string) =>
        list.filter((m) =>
          m.incidentPositions?.some((p) => p.positionId === positionId)
        );

      const seatData: { id: string; data: SeatBoxNodeData }[] = visible.map(
        (pos) => {
          const activeForSeat = showActive
            ? membersFor(incidentMembers, pos.id)
            : [];
          const draftForSeat = usingHwcgPeriods
            ? membersFor(hwcgLaterMembers ?? [], pos.id)
            : showDraft
              ? membersFor(draftIncidentMembers ?? [], pos.id)
              : [];
          const seatStatus = hwcgSeatStatusCounts(
            [...activeForSeat, ...draftForSeat].map((member) => member.userId),
            hwcgPersonStatus
          );
          return {
            id: pos.id,
            data: {
              positionId: pos.id,
              name: pos.name,
              isWorkgroup: pos.is_workgroup,
              isResourcePosition: pos.isResourcePosition,
              resourceType: pos.resourceType,
              activeMembers: activeForSeat,
              draftMembers: draftForSeat,
              periodLabels: hwcgPeriodLabels,
              hwcgStatusLine: seatStatus
                ? hwcgSeatStatusLine(seatStatus)
                : null,
              activeResourceCount: showActive
                ? activeSeatResources.filter((r) => r.positionId === pos.id)
                    .length
                : 0,
              draftResourceCount: showDraft
                ? draftSeatResources.filter((r) => r.positionId === pos.id)
                    .length
                : 0,
              labelRosters: usingHwcgPeriods
                ? activeForSeat.length > 0 && draftForSeat.length > 0
                : labelRosters,
              retireNextOp: !!pos.retire_next_op,
              categoryLabel: resolveCategoryLabel(
                pos.category,
                pos.custom_category
              ),
              sectionTheme: getSectionTheme(pos.id, posById),
              onOpen: openModal,
              onAddChildPosition: addChildPosition,
              onAddChildResource: addChildResource,
              canOpen: canOpen(pos.id),
              readOnly,
              childCount: collapse.childCount.get(pos.id) ?? 0,
              collapsed: collapsedIds.has(pos.id),
              hiddenCount: collapse.hiddenCount.get(pos.id) ?? 0,
              onToggleCollapse: toggleCollapsed,
            },
          };
        }
      );

      const hierarchy = visible
        .filter((pos) => pos.parent_id && visibleIds.has(pos.parent_id))
        .map((pos) => ({ source: pos.parent_id!, target: pos.id }));

      // Real measurements once the boxes have rendered, the estimate until then.
      // The estimate is only ever a first paint: it cannot know that a long email
      // wraps onto a second line, and a seat that renders taller than its guess is
      // what used to land on top of the sibling beneath it.
      const layoutNodes: LayoutNode[] = seatData.map((seat) => {
        const measured = measuredSizes[seat.id];
        return {
          id: seat.id,
          parentId: posById.get(seat.id)?.parent_id ?? null,
          name: seat.data.name,
          width: measured?.width ?? NODE_WIDTH,
          height: measured?.height ?? estimateNodeHeight(seat.data),
        };
      });

      // The ICS default places every seat. Nothing overrides it but a restyle from
      // the toolbar, which lives for the session only — seats are no longer
      // positioned by hand, so there are no saved coordinates to honour. A
      // restyled chart is laid out entirely by that restyle: mixing in an ICS
      // coordinate for one seat is what used to drop a new box on top of another.
      const icsLayout = styleLayout
        ? null
        : layoutIcsDefaultOrgChart({ nodes: layoutNodes });

      const styledPosition = (id: string): FlowPoint | undefined => {
        if (!styleLayout) return undefined;
        const own = styleLayout[id];
        if (own) return own;
        // A seat added while a restyle is in force has no coordinate of its own
        // until the relayout effect runs. One frame on its parent reads as the box
        // appearing on that branch rather than flying in from somewhere else.
        const parentId = posById.get(id)?.parent_id;
        return parentId ? styleLayout[parentId] : undefined;
      };

      const nodes: ChartNode[] = seatData.map((seat) => ({
        id: seat.id,
        position: styledPosition(seat.id) ??
          icsLayout?.layoutPositions[seat.id] ?? { x: 0, y: 0 },
        data: seat.data,
      }));

      const sizeById = new Map(
        layoutNodes.map((node) => [
          node.id,
          { width: node.width, height: node.height },
        ])
      );
      const rects: ChartRect[] = nodes.map((node) => ({
        id: node.id,
        x: node.position.x,
        y: node.position.y,
        ...(sizeById.get(node.id) ?? { width: NODE_WIDTH, height: 0 }),
      }));

      return {
        nodes,
        rects,
        layoutNodes,
        hierarchy,
        collapsible: collapse.collapsible,
        icsEdgeRoutes: icsLayout?.edgeRoutes ?? EMPTY_EDGE_ROUTES,
        icsEdgeAttachments:
          icsLayout?.edgeAttachments ?? EMPTY_EDGE_ATTACHMENTS,
      };
    }, [
      incidentPositionsQuery.data,
      incidentMembers,
      draftIncidentMembers,
      hwcgLaterMembers,
      hwcgPeriodLabels,
      hwcgPersonStatus,
      activeSeatResources,
      draftSeatResources,
      showActive,
      showDraft,
      keepEmpty,
      labelRosters,
      interactionMode,
      readOnly,
      visiblePositionIds,
      editablePositionIds,
      styleLayout,
      measuredSizes,
      collapsedIds,
      toggleCollapsed,
    ]);

    /**
     * Every reporting line, routed clear of every box it does not join. Session
     * overrides win per edge; otherwise the ICS elbows are used, and whatever
     * the layout drew through a seat is rerouted around it.
     */
    const connectors = useMemo(() => {
      const nameById = new Map(
        chartModel.nodes.map((node) => [node.id, node.data.name])
      );
      const routed = routeConnectors({
        rects: chartModel.rects,
        connectors: chartModel.hierarchy.map(({ source, target }) => {
          const id = rosterEdgeId(source, target);
          const attachments =
            edgeAttachments[id] ?? chartModel.icsEdgeAttachments[id];
          return {
            id,
            source,
            target,
            waypoints: edgeRoutes[id] ?? chartModel.icsEdgeRoutes[id],
            sourceAttach: attachments?.source,
            targetAttach: attachments?.target,
          };
        }),
      });
      return routed.map((connector) => {
        const targetName = nameById.get(connector.target) ?? '';
        return {
          ...connector,
          path: polylineToPath(connector.points),
          dashed: isTemaLouSeat(targetName) || isTemaRccSeat(targetName),
        };
      });
    }, [chartModel, edgeRoutes, edgeAttachments]);

    useImperativeHandle(
      ref,
      () => ({
        captureImage: () =>
          captureOrgChartImage(
            chartModel.rects,
            containerRef.current,
            viewportElementRef.current
          ),
      }),
      [chartModel.rects]
    );

    /**
     * Keep a restyled chart in its style as seats come and go. Adding a
     * position or a resource brings a seat the restyle has never placed, so the
     * branch it joins is laid out again and the collision pass gets to separate
     * it from its neighbours; a deleted seat just leaves the session layout.
     */
    useEffect(() => {
      if (!styleLayout) return;

      const liveIds = new Set(chartModel.layoutNodes.map((node) => node.id));
      const added = chartModel.layoutNodes.filter(
        (node) => !styleLayout[node.id]
      );
      const removed = Object.keys(styleLayout).filter((id) => !liveIds.has(id));
      if (added.length === 0 && removed.length === 0) return;

      if (added.length === 0) {
        setStyleLayout((current) => {
          if (!current) return current;
          const next = { ...current };
          for (const id of removed) delete next[id];
          return next;
        });
        return;
      }

      // Relaying out from the parent puts the new seat on the same rail as its
      // siblings. A new seat with no parent on the chart stands in for itself.
      const branchRoots = [
        ...new Set(
          added.map((node) =>
            node.parentId && liveIds.has(node.parentId)
              ? node.parentId
              : node.id
          )
        ),
      ];

      const result = applyRosterLayoutStyle({
        style: layoutStyle,
        nodes: chartModel.layoutNodes,
        selectedIds: branchRoots,
        existingLayout: styleLayout,
        existingEdgeRoutes: edgeRoutes,
        existingEdgeAttachments: edgeAttachments,
      });

      setStyleLayout(result.layoutPositions);
      setEdgeRoutes(result.edgeRoutes);
      setEdgeAttachments(result.edgeAttachments);
    }, [
      chartModel.layoutNodes,
      styleLayout,
      layoutStyle,
      edgeRoutes,
      edgeAttachments,
    ]);

    // Selection is worth keeping across a rebuild: the whole point of picking
    // seats is to then act on them, and a background refetch should not undo
    // that. Only seats that have left the chart drop out of it.
    const selectedIds = useMemo<ReadonlySet<string>>(() => {
      if (pickedIds.size === 0) return pickedIds;
      const live = new Set(chartModel.nodes.map((node) => node.id));
      const next = new Set([...pickedIds].filter((id) => live.has(id)));
      return next.size === pickedIds.size ? pickedIds : next;
    }, [pickedIds, chartModel.nodes]);

    // Unlike a refetch, collapsing a branch is a deliberate hide, so seats in it
    // stay unselected when the branch is expanded again.
    const collapsedForPickRef = useRef(collapsedIds);
    useEffect(() => {
      if (collapsedForPickRef.current === collapsedIds) return;
      collapsedForPickRef.current = collapsedIds;
      if (selectedIds !== pickedIds) setPickedIds(selectedIds);
    }, [collapsedIds, selectedIds, pickedIds]);

    useEffect(() => {
      onSelectedPositionIdChange?.(
        selectedIds.size === 1 ? [...selectedIds][0] : null
      );
    }, [selectedIds, onSelectedPositionIdChange]);

    // Fit once the chart has seats, and again once every card has reported its
    // real size, since the layout moves when the estimates are corrected.
    const hasFitRef = useRef(false);
    const hasFitMeasuredRef = useRef(false);
    const allMeasured =
      chartModel.nodes.length > 0 &&
      chartModel.nodes.every((node) => measuredSizes[node.id]);
    useEffect(() => {
      if (chartModel.rects.length === 0) return;
      if (!hasFitRef.current) {
        hasFitRef.current = true;
        fitBounds(boundsOfRects(chartModel.rects));
        return;
      }
      if (allMeasured && !hasFitMeasuredRef.current) {
        hasFitMeasuredRef.current = true;
        fitBounds(boundsOfRects(chartModel.rects));
      }
    }, [chartModel.rects, allMeasured, fitBounds]);

    const fitView = useCallback(
      () => fitBounds(boundsOfRects(chartModel.rects)),
      [fitBounds, chartModel.rects]
    );

    const clearSelection = useCallback(() => {
      setPickedIds((current) => (current.size === 0 ? current : NO_SELECTION));
    }, []);

    const switchToSelectMode = useCallback(() => {
      setInteractionMode('select');
      clearSelection();
    }, [clearSelection]);

    useEffect(() => {
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') switchToSelectMode();
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [switchToSelectMode]);

    const toggleSelected = useCallback((id: string) => {
      setPickedIds((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);

    const handleSeatClick = useCallback(
      (id: string, event: React.MouseEvent) => {
        if (readOnly) return;
        // The add-child menu is portalled, so its clicks bubble here through
        // React without being inside the seat; they are not a pick.
        if (!event.currentTarget.contains(event.target as Node)) return;
        if (event.shiftKey || isBoxMode) {
          toggleSelected(id);
          return;
        }
        setPickedIds(new Set([id]));
      },
      [readOnly, isBoxMode, toggleSelected]
    );

    /** Seats whose box, on screen, overlaps the marquee at all. */
    const seatsUnder = useCallback(
      (area: ScreenRect): string[] => {
        const { x, y, zoom } = viewportRef.current;
        return chartModel.rects
          .filter((rect) =>
            rectsIntersect(area, {
              x: rect.x * zoom + x,
              y: rect.y * zoom + y,
              width: rect.width * zoom,
              height: rect.height * zoom,
            })
          )
          .map((rect) => rect.id);
      },
      [chartModel.rects, viewportRef]
    );

    const onCanvasPointerDown = useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        // A seat's menu is portalled to <body>, so its presses reach this
        // handler through React's tree while sitting outside the canvas in the
        // DOM. Capturing that pointer would retarget the click away from the
        // menu item. Only a press physically on the canvas is a pan or marquee.
        if (!event.currentTarget.contains(target)) return;
        // A press on a seat or on the toolbars belongs to them.
        if (
          target.closest(`[${SEAT_ATTRIBUTE}]`) ||
          target.closest(`[${CHROME_ATTRIBUTE}]`)
        )
          return;

        const primary = event.button === 0;
        const auxiliary = event.button === 1 || event.button === 2;
        if (!primary && !auxiliary) return;

        const point = toContainerPoint(event.clientX, event.clientY);
        if (primary && isBoxMode && !readOnly) {
          dragRef.current = {
            kind: 'marquee',
            pointerId: event.pointerId,
            startX: point.x,
            startY: point.y,
            base: event.shiftKey ? selectedIds : NO_SELECTION,
          };
          setMarquee({ x: point.x, y: point.y, width: 0, height: 0 });
        } else {
          dragRef.current = {
            kind: 'pan',
            pointerId: event.pointerId,
            lastX: event.clientX,
            lastY: event.clientY,
            moved: false,
            button: event.button,
          };
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
      },
      [isBoxMode, readOnly, selectedIds, toContainerPoint]
    );

    const onCanvasPointerMove = useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;

        if (drag.kind === 'pan') {
          const dx = event.clientX - drag.lastX;
          const dy = event.clientY - drag.lastY;
          if (
            !drag.moved &&
            Math.abs(dx) < DRAG_THRESHOLD &&
            Math.abs(dy) < DRAG_THRESHOLD
          )
            return;
          if (!drag.moved) setIsPanning(true);
          drag.moved = true;
          drag.lastX = event.clientX;
          drag.lastY = event.clientY;
          panBy(dx, dy);
          return;
        }

        const point = toContainerPoint(event.clientX, event.clientY);
        const area: ScreenRect = {
          x: Math.min(drag.startX, point.x),
          y: Math.min(drag.startY, point.y),
          width: Math.abs(point.x - drag.startX),
          height: Math.abs(point.y - drag.startY),
        };
        setMarquee(area);
        setPickedIds(new Set([...drag.base, ...seatsUnder(area)]));
      },
      [panBy, seatsUnder, toContainerPoint]
    );

    const onCanvasPointerUp = useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);

        if (drag.kind === 'marquee') {
          setMarquee(null);
          return;
        }
        setIsPanning(false);
        // A left click on empty canvas, with no drag, drops the selection.
        if (!drag.moved && drag.button === 0 && !readOnly) clearSelection();
      },
      [readOnly, clearSelection]
    );

    const onCanvasDoubleClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        if (target.closest(`[${CHROME_ATTRIBUTE}]`)) return;
        event.preventDefault();
        zoomInAt(toContainerPoint(event.clientX, event.clientY));
      },
      [zoomInAt, toContainerPoint]
    );

    const applyLayoutStyle = useCallback(
      (style: RosterLayoutStyle) => {
        if (chartModel.layoutNodes.length === 0) return;

        const existingLayout = Object.fromEntries(
          chartModel.nodes.map((node) => [node.id, { ...node.position }])
        );

        const {
          layoutPositions,
          edgeRoutes: routes,
          edgeAttachments: attachments,
        } = applyRosterLayoutStyle({
          style,
          nodes: chartModel.layoutNodes,
          selectedIds: [...selectedIds],
          existingLayout,
          existingEdgeRoutes: edgeRoutes,
          existingEdgeAttachments: edgeAttachments,
        });

        setLayoutStyle(style);
        setEdgeRoutes(routes);
        setEdgeAttachments(attachments);
        // Held rather than persisted, so the memo can reapply it when the roster
        // refetches without the chart snapping back to the ICS default.
        setStyleLayout(layoutPositions);
      },
      [chartModel, selectedIds, edgeRoutes, edgeAttachments]
    );

    const resetLayout = useCallback(() => {
      setEdgeRoutes({});
      setEdgeAttachments({});
      setLayoutStyle('centered');
      setStyleLayout(null);
    }, []);

    // Read the open resource off the live query rather than the click, so an edit refreshes the modal and a detach
    // closes it.
    const openResourcePosition: IncidentPosition | null =
      (incidentPositionsQuery.data ?? []).find(
        (pos) => pos.id === openResourcePositionId && pos.isResourcePosition
      ) ?? null;

    const chrome = { [CHROME_ATTRIBUTE]: 'true' };

    return (
      <RosterChartColorModeContext.Provider value={colorMode}>
        <div className="form-block chart" style={{ width: '100%' }}>
          {readOnly ? null : (
            <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-3">
              <Button
                variant="outline"
                size="sm"
                disabled={!hasCustomLayout}
                title={
                  hasCustomLayout
                    ? 'Put every seat back where the automatic layout would place it'
                    : 'The chart is already on the automatic layout'
                }
                onClick={resetLayout}
              >
                Reset Layout
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={collapsedIds.size === 0}
                title="Show every seat on the chart"
                onClick={() => updateCollapsed(() => NO_SELECTION)}
              >
                <ChevronsUpDown />
                Expand all
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={chartModel.collapsible.length === 0}
                title="Hide every seat below the top two levels of the chart"
                onClick={() =>
                  updateCollapsed(
                    (current) =>
                      new Set([...current, ...chartModel.collapsible])
                  )
                }
              >
                <ChevronsDownUp />
                Collapse all
              </Button>
            </div>
          )}

          <div
            ref={containerRef}
            role="application"
            aria-label="Incident organization chart"
            className={classNames(
              'tw:relative tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:select-none tw:touch-none',
              colorMode === 'light' ? 'tw:bg-white' : 'tw:bg-background',
              isPanning
                ? 'tw:cursor-grabbing'
                : isBoxMode
                  ? 'tw:cursor-crosshair'
                  : 'tw:cursor-grab'
            )}
            style={{
              height: '600px',
              width: '100%',
              backgroundImage: `radial-gradient(circle, ${DOT_GRID_COLOR} ${DOT_GRID_RADIUS}px, transparent ${DOT_GRID_RADIUS + 0.5}px)`,
              backgroundSize: `${DOT_GRID_GAP * viewport.zoom}px ${DOT_GRID_GAP * viewport.zoom}px`,
              backgroundPosition: `${viewport.x}px ${viewport.y}px`,
            }}
            onPointerDown={onCanvasPointerDown}
            onPointerMove={onCanvasPointerMove}
            onPointerUp={onCanvasPointerUp}
            onPointerCancel={onCanvasPointerUp}
            onDoubleClick={onCanvasDoubleClick}
            // Right-drag pans, so the browser menu must not open on release.
            onContextMenu={(event) => event.preventDefault()}
          >
            {/* Everything in chart coordinates lives on this layer; the pan and
                zoom are one transform on it, and the ICS-207 capture photographs
                it with a fit transform of its own. */}
            <div
              ref={viewportElementRef}
              className="tw:absolute tw:inset-0 tw:origin-top-left"
              style={{
                transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
              }}
            >
              <svg
                className="tw:pointer-events-none tw:absolute tw:top-0 tw:left-0 tw:h-full tw:w-full"
                style={{ overflow: 'visible' }}
                aria-hidden
              >
                {connectors.map((connector) => (
                  <path
                    key={connector.id}
                    d={connector.path}
                    fill="none"
                    stroke={EDGE_STROKE_COLOR}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    strokeDasharray={connector.dashed ? '6 4' : undefined}
                  />
                ))}
              </svg>
              {chartModel.nodes.map((node) => (
                <div
                  key={node.id}
                  ref={seatRef(node.id)}
                  {...{ [SEAT_ATTRIBUTE]: node.id }}
                  className="tw:absolute tw:top-0 tw:left-0"
                  style={{
                    width: NODE_WIDTH,
                    transform: `translate(${node.position.x}px, ${node.position.y}px)`,
                  }}
                  onClick={(event) => handleSeatClick(node.id, event)}
                >
                  <SeatBoxCard
                    data={node.data}
                    selected={selectedIds.has(node.id)}
                  />
                </div>
              ))}
            </div>

            {marquee ? (
              <div
                className="tw:pointer-events-none tw:absolute tw:border tw:border-sky-400 tw:bg-sky-400/10"
                style={{
                  left: marquee.x,
                  top: marquee.y,
                  width: marquee.width,
                  height: marquee.height,
                }}
              />
            ) : null}

            <div
              {...chrome}
              className={classNames(
                'tw:absolute tw:top-2 tw:left-2',
                readOnly && 'tw:hidden'
              )}
            >
              <div className={TOOLBAR_GROUP_CLASS}>
                {LAYOUT_STYLE_OPTIONS.map((option) => (
                  <Button
                    key={option.id}
                    size="xs"
                    className="tw:text-xs!"
                    variant={layoutStyle === option.id ? 'secondary' : 'ghost'}
                    title={option.hint}
                    onClick={() => applyLayoutStyle(option.id)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
            <div
              {...chrome}
              className={classNames(
                'tw:absolute tw:top-2 tw:right-2',
                readOnly && 'tw:hidden'
              )}
            >
              <div className={TOOLBAR_GROUP_CLASS}>
                <Button
                  size="xs"
                  className="tw:text-xs!"
                  variant={isBoxMode ? 'ghost' : 'secondary'}
                  title="Left-drag pans the chart; click a seat to select and open it"
                  onClick={switchToSelectMode}
                >
                  <MousePointer2 />
                  Select
                </Button>
                <Button
                  size="xs"
                  className="tw:text-xs!"
                  variant={isBoxMode ? 'secondary' : 'ghost'}
                  title="Left-drag draws a rectangle to select seats; Escape clears it"
                  onClick={() => setInteractionMode('box')}
                >
                  <SquareDashed />
                  Select box
                </Button>
              </div>
            </div>
            <div {...chrome} className="tw:absolute tw:bottom-2 tw:left-2">
              <div className={classNames(TOOLBAR_GROUP_CLASS, 'tw:flex-col')}>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  title="Zoom in"
                  aria-label="Zoom in"
                  onClick={zoomIn}
                >
                  <ZoomIn />
                </Button>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  title="Zoom out"
                  aria-label="Zoom out"
                  onClick={zoomOut}
                >
                  <ZoomOut />
                </Button>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  title="Fit the whole chart in view"
                  aria-label="Fit view"
                  onClick={fitView}
                >
                  <Maximize />
                </Button>
              </div>
            </div>
          </div>

          {openResourcePosition && (
            <RosterResourceModal
              show
              position={openResourcePosition}
              onClose={() => setOpenResourcePositionId('')}
            />
          )}

          {showEditPositionModalForPosition !== '' && (
            <EditPositionModal
              show
              onHide={() => setShowEditPositionModal('')}
              positionId={showEditPositionModalForPosition}
              activeMembers={incidentMembers}
              draftMembers={draftIncidentMembers}
              initialTab={seatModalTab}
            />
          )}

          {addChildPositionParentId ? (
            <AddChildPositionDialog
              key={addChildPositionParentId}
              show
              parentPositionId={addChildPositionParentId}
              onClose={() => setAddChildPositionParentId(null)}
            />
          ) : null}

          {addChildResourceParentId ? (
            <AddResourceToRosterDialog
              key={addChildResourceParentId}
              show
              defaultReportsToPositionId={addChildResourceParentId}
              onClose={() => setAddChildResourceParentId(null)}
            />
          ) : null}
        </div>
      </RosterChartColorModeContext.Provider>
    );
  }
);

export default RosterOrgChart;
